import type {
  KgInsightCohortCell,
  KgInsightCohortCellByType,
  KgInsightCohortColumn,
  KgInsightCohortRow,
} from '@api';
import { SkeletonLoader } from '@design';
import {
  type ColumnConfig,
  ColumnTable,
  type ColumnTableRow,
} from '@terminal/components/column-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CatalystPriceChange } from '../catalyst-price-change/CatalystPriceChange';
import styles from './CohortTable.module.scss';

type AnyRenderer = (cell: KgInsightCohortCell) => React.ReactNode;

const PriceMoveCell = ({
  entityTag,
  liveDataText,
  lastCloseText,
  catalystPublicationDate,
}: {
  entityTag: string;
  liveDataText: string;
  lastCloseText: string;
  catalystPublicationDate: string;
}) => {
  const { data, isFetching } = useEntity(entityTag);

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '36px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!data?.data) {
    return null;
  }

  return (
    <CatalystPriceChange
      className={styles.priceChange}
      entity={data.data}
      catalystPublicationDate={catalystPublicationDate}
      liveDataText={liveDataText}
      lastCloseText={lastCloseText}
    />
  );
};
export interface CohortTableProps {
  columns: KgInsightCohortColumn[];
  rows: KgInsightCohortRow[];
  catalystPublicationDate: string;
}

type CohortColumnType = KgInsightCohortColumn['type'];

function renderCell({
  colType,
  value,
}: {
  colType: CohortColumnType;
  value: KgInsightCohortCell | null;
}): React.ReactNode {
  if (!value) {
    return null;
  }

  const cellRenderers: {
    [K in Exclude<CohortColumnType, 'price_move'>]: (
      cell: KgInsightCohortCellByType[K]
    ) => React.ReactNode;
  } = {
    entity: cell => <EntityCell entityTag={cell.value} />,
    text: cell => <span className={styles.textCell}>{cell.value}</span>,
  };

  return (cellRenderers[colType] as AnyRenderer)(value);
}

export const CohortTable = ({
  columns,
  rows,
  catalystPublicationDate,
}: CohortTableProps) => {
  const { navigateToAssetOverview } = useNavigateToAssetOverview();

  const { t } = useTranslation(['countries', 'insights']);
  const liveDataText = t(
    'insights:catalystGeneration.reflexivityGraph.liveData'
  );
  const lastCloseText = t(
    'insights:catalystGeneration.reflexivityGraph.asOfLastClose'
  );
  const priceMoveColumnLabel = t(
    'insights:catalystGeneration.reflexivityGraph.priceMove'
  );
  const tableColumns = useMemo(() => {
    const tableColumns: ColumnConfig<KgInsightCohortRow>[] = columns.map(
      col => ({
        key: col.key,
        label: col.label,
        render: value =>
          renderCell({
            colType: col.type,
            value,
          }),
      })
    );

    const priceMoveColumn: ColumnConfig<KgInsightCohortRow> = {
      key: 'price_move',
      label: priceMoveColumnLabel,
      alignment: 'end',
      render: (_value, item) => (
        <PriceMoveCell
          liveDataText={liveDataText}
          lastCloseText={lastCloseText}
          catalystPublicationDate={catalystPublicationDate}
          entityTag={item.asset.value}
        />
      ),
    };

    return [...tableColumns, priceMoveColumn];
  }, [
    columns,
    liveDataText,
    lastCloseText,
    priceMoveColumnLabel,
    catalystPublicationDate,
  ]);

  const data = useMemo(
    () =>
      rows.map(
        row =>
          ({
            ...row,
            key: String(row.asset.value),
          }) as ColumnTableRow<KgInsightCohortRow>
      ),
    [rows]
  );

  const handleRowClick = (item: ColumnTableRow<KgInsightCohortRow>) => {
    navigateToAssetOverview(
      item.asset.value,
      POSTHOG_TRACKING_SOURCES.KG_INSIGHT_PAGE
    );
  };

  return (
    <div className={styles.root}>
      <ColumnTable<KgInsightCohortRow>
        columns={tableColumns}
        data={data}
        onRowClick={handleRowClick}
      />
    </div>
  );
};
